import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/case-article/editor/ImageUpload";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Plus, X } from "lucide-react";
import { useEditableCategories } from "@/hooks/useEditableCategories";

export default function AdminComponentEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [mainCategory, setMainCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [finish, setFinish] = useState("");
  const [colors, setColors] = useState<string[]>([]);
  const [newColor, setNewColor] = useState("#000000");

  const { data: component } = useQuery({
    queryKey: ["admin-component", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase.from("components").select("*").eq("id", id!).single();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (component) {
      setName(component.name);
      setMainCategory(component.main_category);
      setSubCategory(component.sub_category);
      setImageUrl(component.image_url || "");
      setFinish(component.finish || "");
      setColors(component.colors || []);
    }
  }, [component]);

  const { categories: editableCategories } = useEditableCategories();
  const selectedMainCat = editableCategories.find((c) => c.id === mainCategory);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        main_category: mainCategory,
        sub_category: subCategory,
        image_url: imageUrl || null,
        finish: finish || null,
        colors,
      };

      if (isNew) {
        const { error } = await supabase.from("components").insert(payload);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("components").update(payload).eq("id", id!);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({ title: "Saved", description: "Component saved successfully." });
      queryClient.invalidateQueries({ queryKey: ["admin-components"] });
      if (isNew) navigate("/admin/components");
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const addColor = () => {
    if (newColor && !colors.includes(newColor)) {
      setColors([...colors, newColor]);
    }
  };

  const removeColor = (color: string) => {
    setColors(colors.filter((c) => c !== color));
  };

  return (
    <Layout>
      <div className="min-h-screen bg-muted">
        <div className="mx-auto max-w-[900px] px-6 pt-24 pb-12">
          <div className="flex items-center gap-4 mb-10">
            <Button asChild variant="ghost" size="icon">
              <Link to="/admin/components"><ArrowLeft size={18} /></Link>
            </Button>
            <h1 className="font-serif text-2xl text-foreground flex-1">
              {isNew ? "New Component" : "Edit Component"}
            </h1>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !name || !mainCategory || !subCategory}
              className="gap-2 font-sans text-xs uppercase tracking-wider"
            >
              <Save size={14} /> {saveMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </div>

          <div className="bg-background border border-border p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="font-sans text-xs">Name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="font-sans text-xs">Finish</Label>
                <Input value={finish} onChange={(e) => setFinish(e.target.value)} placeholder="e.g. Polished, Matte" className="mt-1" />
              </div>
              <div>
                <Label className="font-sans text-xs">Main Category *</Label>
                <Select value={mainCategory} onValueChange={(v) => { setMainCategory(v); setSubCategory(""); }}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {editableCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-sans text-xs">Sub Category *</Label>
                <Input value={subCategory} onChange={(e) => setSubCategory(e.target.value)} placeholder="e.g. Porcelain Tiles, Hardwood" className="mt-1" />
              </div>
            </div>

            <div>
              <ImageUpload label="Component Image" value={imageUrl} onChange={setImageUrl} />
            </div>

            <div>
              <Label className="font-sans text-xs mb-2 block">Colors</Label>
              <div className="flex flex-wrap gap-2 mb-3">
                {colors.map((color) => (
                  <div key={color} className="flex items-center gap-1 rounded-full border border-border px-2 py-1">
                    <div className="h-4 w-4 rounded-full border" style={{ backgroundColor: color }} />
                    <span className="text-xs text-muted-foreground">{color}</span>
                    <button onClick={() => removeColor(color)} className="ml-1 text-muted-foreground hover:text-destructive">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="h-8 w-8 cursor-pointer rounded border border-border"
                />
                <Input value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-28" />
                <Button type="button" variant="outline" size="sm" onClick={addColor} className="gap-1">
                  <Plus size={12} /> Add
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
